import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Card,
  CardBody,
  Flex,
  Heading,
  HStack,
  Skeleton,
  Stack,
  Tag,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiFileText, FiHelpCircle, FiMessageCircle, FiShare2, FiUserCheck } from "react-icons/fi";
import { Link as RouterLink, Navigate, useLocation, useParams } from "react-router-dom";
import {
  type PortalMarketDialogueDto,
  useGetPortalMarketDialogueQuery,
  useGetSupplierMarketDialogueQuery,
} from "../../../autogen/bff-api";
import { currentPathSuffix } from "../../../common/auth/returnTo";
import { isNotFoundError } from "../../../common/errors/isNotFoundError";
import { useApiError } from "../../../common/errors/useApiError";
import { momentLocaleFor } from "../../../common/formatting/i18nFormatters";
import { timeLeftUntil } from "../../../common/formatting/timeLeft";
import { usePortalEventUrls } from "../../../common/sourcing-events/portal/usePortalEventUrls";
import { isWellFormedId } from "../../../common/ids/isWellFormedId";
import { TipTap } from "../../../common/input/TipTap/TipTap";
import { useRecordTitle } from "../../../common/page-title/PageTitle";
import { useAppSelector } from "../../../common/redux/hooks";
import { urls } from "../../../urls";
import { PortalLayout } from "../../sourcing-events/portal/components/PortalLayout";
import { BackToListingLink } from "../../sourcing-events/portal/view-single/BackToListingLink";
import { SectionCard } from "../../sourcing-events/portal/view-single/SectionCard";
import { SharedWithParticipants } from "../SharedWithParticipants";
import { SupplierQuestions } from "./SupplierQuestions";
import { DeadlineHighlightCard } from "../../sourcing-events/portal/view-single/EventDeadlineHighlight";
import { BuyerBadge, MetaSeparator } from "../../sourcing-events/portal/view-single/EventHero";
import { Frame } from "../../sourcing-events/portal/view-single/ParticipationCta";
import { formatDeadline } from "../../sourcing-events/portal/utils/displayFormatters";
import { MarketDialogueQuestionList } from "../MarketDialogueQuestions";
import { MarketDialogueAnswerForm } from "./MarketDialogueAnswerForm";
import { PortalMarketDialogueDocuments } from "./PortalMarketDialogueDocuments";

export const SupplierMarketDialoguePage = () => {
  const { t } = useTranslation();
  const { marketDialogueId = "" } = useParams();
  const displayer = useApiError();
  const authState = useAppSelector((state) => state.authState.state);
  const loggedInWithOrg = authState?.type === "LoggedInWithOrgContext";
  const isBrokenLink = !isWellFormedId(marketDialogueId);

  const anonymous = useGetPortalMarketDialogueQuery(
    { marketDialogueId },
    { skip: isBrokenLink || loggedInWithOrg },
  );
  const supplier = useGetSupplierMarketDialogueQuery(
    { marketDialogueId },
    { skip: isBrokenLink || !loggedInWithOrg },
  );
  const { error, isLoading } = loggedInWithOrg ? supplier : anonymous;
  const dialogue = loggedInWithOrg ? supplier.data?.dialogue : anonymous.data;
  useRecordTitle(dialogue?.title);

  if (supplier.data?.viewerOwnsDialogue) {
    return <Navigate to={urls.app.marketDialogues.view.go(marketDialogueId)} replace />;
  }

  const content = () => {
    if (isBrokenLink || isNotFoundError(error)) return <NotFoundCard />;
    if (error) return displayer.show(error);
    if (isLoading || !dialogue) return <Skeleton height="200px" rounded="xl" startColor="smBackground" endColor="smBackgroundTertiary" />;
    return (
      <>
        <DialogueHero dialogue={dialogue} />
        <DialogueDeadline dialogue={dialogue} />
        {supplier.data ? (
          <>
            <DialogueDescription dialogue={dialogue} />
            <PortalMarketDialogueDocuments dialogue={dialogue} asSupplier />
            <SharedSection dialogue={dialogue} />
            <MarketDialogueAnswerForm
              key={supplier.data.dialogue.id}
              supplierDialogue={supplier.data}
              refetch={() => void supplier.refetch()}
            />
            <SectionCard icon={FiMessageCircle} title={t("Questions to the buyer")} count={supplier.data.questions?.length ?? 0}>
              <SupplierQuestions
                marketDialogueId={dialogue.id}
                questions={supplier.data.questions ?? []}
                canAsk={supplier.data.canAnswer}
                onAsked={() => void supplier.refetch()}
              />
            </SectionCard>
          </>
        ) : (
          <>
            {!dialogue.deadlinePassed && <LogInToAnswer />}
            <DialogueDescription dialogue={dialogue} />
            <SectionCard icon={FiHelpCircle} title={t("Questions to the suppliers")} count={dialogue.questions.length}>
              <MarketDialogueQuestionList questions={dialogue.questions} />
            </SectionCard>
            <PortalMarketDialogueDocuments dialogue={dialogue} />
            <SharedSection dialogue={dialogue} />
          </>
        )}
      </>
    );
  };

  return (
    <PortalLayout>
      <Stack spacing="6">
        <BackToListingLink />
        {content()}
      </Stack>
    </PortalLayout>
  );
};

const SharedSection = ({ dialogue }: { dialogue: PortalMarketDialogueDto }) => {
  const { t } = useTranslation();
  const shared = dialogue.shared ?? [];
  if (shared.length === 0) return null;
  return (
    <SectionCard icon={FiShare2} title={t("Shared with every participant")} count={shared.length}>
      <SharedWithParticipants shared={shared} documents={dialogue.documents} />
    </SectionCard>
  );
};

const DialogueHero = ({ dialogue }: { dialogue: PortalMarketDialogueDto }) => {
  const { t } = useTranslation();
  return (
    <Stack spacing="4">
      <Heading as="h1" fontSize={{ base: "3xl", md: "4xl" }} fontWeight="bold" lineHeight="1.2">
        {dialogue.title}
      </Heading>
      <HStack spacing="3" align="center" flexWrap="wrap" rowGap="2">
        <Tag variant="outline" colorScheme="teal" size="sm">
          {t("Market dialogue")}
        </Tag>
        {dialogue.round.number > 1 && (
          <Tag variant="subtle" colorScheme="teal" size="sm">
            {t("Round {{number}}", { number: dialogue.round.number })}
          </Tag>
        )}
        <MetaSeparator />
        <BuyerBadge name={dialogue.buyerName} />
      </HStack>
      {dialogue.round.isCommentRound && (
        <Text color="smMuted" data-testid="comment-round-intro">
          {t(
            "The buyer asks for your comments on the requirements, criteria, price form and documents of a planned competition. What was said in earlier rounds of this market dialogue still applies.",
          )}
        </Text>
      )}
    </Stack>
  );
};

const DialogueDeadline = ({ dialogue }: { dialogue: PortalMarketDialogueDto }) => {
  const { t, i18n } = useTranslation();
  if (dialogue.deadlinePassed) {
    return (
      <Alert status="warning" variant="subtle" rounded="md">
        <AlertIcon />
        <AlertDescription>
          {t("The answer deadline has passed. Answers can no longer be sent or changed.")}
        </AlertDescription>
      </Alert>
    );
  }
  if (!dialogue.answerDeadline) return null;
  return (
    <DeadlineHighlightCard
      label={t("Answer deadline")}
      date={formatDeadline(dialogue.answerDeadline, t("at"), momentLocaleFor(i18n.language))}
      timeLeft={timeLeftUntil(dialogue.answerDeadline)}
    />
  );
};

const DialogueDescription = ({ dialogue }: { dialogue: PortalMarketDialogueDto }) => {
  const { t } = useTranslation();
  if (!dialogue.description) return null;
  return (
    <SectionCard icon={FiFileText} title={t("About the planned procurement")}>
      <TipTap content={dialogue.description} editable={false} maxHeight="400px" />
    </SectionCard>
  );
};

const LogInToAnswer = () => {
  const { t } = useTranslation();
  const location = useLocation();
  return (
    <Frame
      icon={FiUserCheck}
      title={t("Want to answer this market dialogue?")}
      body={t("Log in or sign up to answer the questions and send documents to the buyer.")}
    >
      <Button
        as={RouterLink}
        to={urls.loginWithReturnUrl(currentPathSuffix(location))}
        variant="outline"
        colorScheme="gray"
        flexShrink="0"
      >
        {t("Log in to answer")}
      </Button>
    </Frame>
  );
};

const NotFoundCard = () => {
  const { t } = useTranslation();
  const portalUrls = usePortalEventUrls();
  return (
    <Card variant="outline" rounded="xl">
      <CardBody py="20">
        <Flex direction="column" align="center" justify="center" gap="4">
          <Text color="smMuted" textAlign="center">
            {t("The market dialogue does not exist or is not open to suppliers.")}
          </Text>
          <Button as={RouterLink} to={portalUrls.list} variant="outline" colorScheme="gray">
            {t("Back to listing")}
          </Button>
        </Flex>
      </CardBody>
    </Card>
  );
};
